import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { Study } from '../../../../shared/api/hscan/hospital';
import { ButtonWithIcon } from '../../common/components/ButtonWithIcon';
import { StyledDateField } from '../../common/components/Slot/DateFieldSlot';

const ButtonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  gap: 10px;
`;
const PeriodContainer = styled(ButtonWrapper)`
  margin-top: 10px;
`;

export type Period = 'week' | 'month' | 'year' | 'custom';
interface PeriodFilteringButtonsProps {
  setPeriodFilteringFunction: Dispatch<SetStateAction<(study: Study) => void>>;
  reset: boolean;
}
export const PeriodFilteringButtons = ({
  setPeriodFilteringFunction,
  reset,
}: PeriodFilteringButtonsProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.request.imageList.periodFiltering',
  });
  const { t: globalT } = useTranslation('translation');

  const [today] = useState(moment());
  const [oneWeekAgo] = useState(today.clone().subtract(1, 'weeks'));
  const [oneMonthAgo] = useState(today.clone().subtract(1, 'months'));
  const [oneYearAgo] = useState(today.clone().subtract(1, 'years'));
  const [fiveYearsAgo] = useState(today.clone().subtract(5, 'years'));
  const [periodMap] = useState<Record<Exclude<Period, 'custom'>, Moment>>({
    week: oneWeekAgo,
    month: oneMonthAgo,
    year: oneYearAgo,
  });

  const [selectedPeriod, setSelectedPeriod] = useState<Period>('year');
  const [from, setFrom] = useState<Moment | null>(oneYearAgo);
  const [to, setTo] = useState<Moment | null>(today);
  const inputRef = useRef(null);
  const onClickPeriodButton = (period: Period) => {
    setSelectedPeriod(period);
  };

  useEffect(() => {
    setSelectedPeriod('year');
    setFrom(oneYearAgo);
    setTo(today);
  }, [reset, oneYearAgo, today]);

  useEffect(() => {
    if (selectedPeriod === 'custom') {
      setPeriodFilteringFunction(() => (study: Study) => {
        return moment(study.date).isBetween(
          from ?? oneYearAgo,
          to ?? today,
          'days',
          '[]',
        );
      });
    } else {
      setPeriodFilteringFunction(() => (study: Study) => {
        return moment(study.date).isBetween(periodMap[selectedPeriod], today);
      });
      setFrom(periodMap[selectedPeriod]);
      setTo(today);
    }
  }, [
    selectedPeriod,
    today,
    oneYearAgo,
    from,
    to,
    periodMap,
    setPeriodFilteringFunction,
  ]);

  return (
    <>
      <ButtonWrapper>
        {['week', 'month', 'year', 'custom'].map(period => (
          <ButtonWithIcon
            key={period}
            $padding="5px"
            text={`${t(period)}`}
            onClick={() => onClickPeriodButton(period as Period)}
            $selected={selectedPeriod === period}
          />
        ))}
      </ButtonWrapper>
      {selectedPeriod === 'custom' && (
        <PeriodContainer>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <StyledDateField
              format={`${globalT('common.dateFormatInputString')}`}
              views={['year', 'month', 'day']}
              value={from}
              minDate={fiveYearsAgo}
              maxDate={today}
              inputRef={inputRef}
              onChange={fromDate => setFrom(fromDate)}
            />
            ~
            <StyledDateField
              format={`${globalT('common.dateFormatInputString')}`}
              views={['year', 'month', 'day']}
              value={to}
              minDate={fiveYearsAgo}
              maxDate={today}
              inputRef={inputRef}
              onChange={toDate => setTo(toDate)}
            />
          </LocalizationProvider>
        </PeriodContainer>
      )}
    </>
  );
};
